/**
 * @file EmptyState.tsx
 * @brief THE empty state: flowing skeleton cards, then a still greyed stack
 *        with a heading and one line. The phone's `VZEmptyState` on the web.
 *
 * @details
 * DES (30-09-2026): "saray placeholder mobile jaisay same karo". Port of
 * mobile `shared/components/VZUIKit/VZEmptyState.tsx`:
 * 1. Three skeleton cards flow through: each slides in from the right and
 *    fades in, holds, then slides out to the left and fades (staggered).
 * 2. The same stack comes back greyed and still, with a 22px bold heading
 *    and one line under it.
 * - `shape` draws the skeleton like the screen's own card (a session's two
 *   people, a meeting's avatar stack, a note's lines, an invoice's amount,
 *   a person, a calendar tile; `row` by default).
 * - `animate={false}` goes straight to the still stack: for "no search
 *   results" / "nothing matches the filters", where a show on every
 *   keystroke would be noise. Reduced motion does the same.
 * - No icon and no dashed box (the old web look); `icon` is accepted and
 *   ignored so older callers keep compiling.
 *
 * ⛔ Mount it only once the list has LOADED and is empty.
 *
 * @author DES
 * @date 26-05-2026
 * @updated 30-09-2026 — the phone's VZEmptyState
 */

import type { LucideIcon } from "lucide-react";
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { cn } from "@/shared/theme/cn";

export type EmptyStateShape =
  | "row"
  | "session"
  | "meeting"
  | "note"
  | "invoice"
  | "person"
  | "calendar";

interface EmptyStateProps {
  title: string;
  description?: string;
  /** Under the text, e.g. an action. */
  action?: ReactNode;
  shape?: EmptyStateShape;
  /** false = the still stack at once (search / filter "no results"). */
  animate?: boolean;
  className?: string;
  /** @deprecated The phone's empty state has no icon; ignored. */
  icon?: LucideIcon;
}

/** The stack, front to back: narrower, shorter and fainter each step. */
const CARDS = [
  { width: "100%", height: 72, opacity: 1 },
  { width: "84%", height: 60, opacity: 0.75 },
  { width: "68%", height: 50, opacity: 0.5 },
] as const;
const CARD_GAP = 14;
const FLOW_MS = 1880;
const IN_MS = 420;
const STAGGER_MS = 130;
/** The illustration is the skeleton stack at this strength. */
const REST_OPACITY = 0.55;

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/** One grey block; it pulses while the stack flows. */
function B({
  w,
  h,
  r,
  pulse,
  style,
}: {
  w: number | string;
  h: number;
  r: number;
  pulse: boolean;
  style?: CSSProperties;
}) {
  return (
    <span
      className={cn(
        // The quaternary token, so it follows the warm page (DES 01-10-2026); was #ECECEC.
        "block shrink-0 bg-app-quaternary dark:bg-white/10",
        pulse && "animate-pulse",
      )}
      style={{ width: w, height: h, borderRadius: r, ...style }}
    />
  );
}

function CardShape({
  shape,
  height,
  pulse,
}: {
  shape: EmptyStateShape;
  height: number;
  pulse: boolean;
}) {
  const d = height - 28; // avatar / tile size
  const lh = Math.max(8, Math.round(height / 7)); // line height
  const lines = (a = "72%", b = "48%") => (
    <span className="flex min-w-0 flex-1 flex-col gap-2">
      <B w={a} h={lh} r={lh / 2} pulse={pulse} />
      <B w={b} h={lh} r={lh / 2} pulse={pulse} />
    </span>
  );
  const pill = <B w={d + 16} h={d - 8} r={999} pulse={pulse} />;

  switch (shape) {
    case "session": {
      const a = Math.round(d * 0.8);
      return (
        <>
          <B w={a} h={a} r={a / 2} pulse={pulse} />
          <B w={a * 0.5} h={Math.max(3, lh / 2)} r={2} pulse={pulse} />
          <B w={a} h={a} r={a / 2} pulse={pulse} />
          {lines("64%", "40%")}
        </>
      );
    }
    case "meeting": {
      const a = Math.round(d * 0.72);
      return (
        <>
          <span className="flex items-center">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="flex items-center justify-center rounded-full bg-app-card"
                style={{ width: a + 4, height: a + 4, marginLeft: i > 0 ? -a * 0.4 : 0 }}
              >
                <B w={a} h={a} r={a / 2} pulse={pulse} />
              </span>
            ))}
          </span>
          {lines("70%", "44%")}
          {pill}
        </>
      );
    }
    case "note":
      return (
        <span className="flex min-w-0 flex-1 flex-col gap-[7px]">
          <B w="46%" h={lh + 2} r={(lh + 2) / 2} pulse={pulse} />
          <B w="92%" h={lh} r={lh / 2} pulse={pulse} />
          <B w="70%" h={lh} r={lh / 2} pulse={pulse} />
        </span>
      );
    case "invoice":
      return (
        <>
          <B w={d} h={d} r={Math.round(d / 4)} pulse={pulse} />
          {lines("62%", "40%")}
          <B w={d + 8} h={lh + 4} r={(lh + 4) / 2} pulse={pulse} />
        </>
      );
    case "person":
      return (
        <>
          <B w={d} h={d} r={d / 2} pulse={pulse} />
          {lines("60%", "82%")}
        </>
      );
    case "calendar":
      return (
        <>
          <span className="flex shrink-0 flex-col items-center justify-center" style={{ width: d, height: d }}>
            <B w={d * 0.5} h={Math.max(4, lh * 0.7)} r={2} pulse={pulse} />
            <B w={d * 0.6} h={lh + 4} r={(lh + 4) / 2} pulse={pulse} style={{ marginTop: 4 }} />
          </span>
          {lines("68%", "44%")}
          {pill}
        </>
      );
    case "row":
    default:
      return (
        <>
          <B w={d} h={d} r={d / 2} pulse={pulse} />
          {lines()}
          {pill}
        </>
      );
  }
}

export function EmptyState({
  title,
  description,
  action,
  shape = "row",
  animate = true,
  className,
}: EmptyStateProps) {
  const [still] = useState(() => !animate || prefersReducedMotion());
  const [phase, setPhase] = useState<"flow" | "rest">(still ? "rest" : "flow");

  // The flow ends when the last card has gone out to the left.
  useEffect(() => {
    if (phase !== "flow") return;
    const t = window.setTimeout(
      () => setPhase("rest"),
      FLOW_MS + (CARDS.length - 1) * STAGGER_MS,
    );
    return () => window.clearTimeout(t);
  }, [phase]);

  const stageHeight =
    CARDS.reduce((h, c) => h + c.height, 0) + CARD_GAP * (CARDS.length - 1);

  return (
    <div
      role="status"
      className={cn("flex flex-col items-center px-4 pb-8 pt-16", className)}
    >
      <div className="flex w-full max-w-[360px] flex-col items-center" style={{ height: stageHeight }}>
        {CARDS.map((c, i) => (
          <div
            key={`${phase}-${i}`}
            className="w-full"
            style={{
              width: c.width,
              marginTop: i > 0 ? CARD_GAP : 0,
              animation: still
                ? undefined
                : phase === "flow"
                  ? `vz-empty-flow ${FLOW_MS}ms both ${i * STAGGER_MS}ms`
                  : `vz-empty-in ${IN_MS}ms cubic-bezier(0.33,1,0.68,1) both ${i * STAGGER_MS}ms`,
            }}
          >
            <div
              className="flex items-center gap-4 rounded-[20px] bg-app-card px-4"
              style={{
                height: c.height,
                opacity: c.opacity * (phase === "rest" ? REST_OPACITY : 1),
              }}
            >
              <CardShape shape={shape} height={c.height} pulse={phase === "flow"} />
            </div>
          </div>
        ))}
      </div>

      {phase === "rest" ? (
        <div
          className="mt-8 flex max-w-[320px] flex-col items-center text-center"
          style={
            still
              ? undefined
              : {
                  animation: `vz-empty-rise ${IN_MS}ms cubic-bezier(0.33,1,0.68,1) both ${CARDS.length * STAGGER_MS}ms`,
                }
          }
        >
          <p className="text-[22px] font-bold tracking-[-0.4px] text-app-text">{title}</p>
          {description ? (
            <p className="mt-2 text-[15px] leading-[21px] text-app-text-secondary">{description}</p>
          ) : null}
          {action ? <div className="mt-4">{action}</div> : null}
        </div>
      ) : (
        /* Holds the text's space so the stack does not jump. */
        <div className="mt-8 h-20" />
      )}
    </div>
  );
}
